import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { countOccupants, countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { FlightsClassTypes, FlightsFareTypes, FlightFilterType } from 'constants/flight'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { Filter_1_0_0, Search_3_0_0, Sort_1_0_0 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import type { StandaloneFlightFiltersState } from 'contexts/Flights/StandaloneFlightsFilters/standaloneFlightsFiltersReducer'
import {
  getAllianceFilterItems,
  getArrivalTimesFilterItems,
  getBagsFilterItems,
  getAirlineFilterItems,
  getCabinTypeFilterItems,
  getDepartureTimesFilterItems,
  getDurationFilterItems,
  getPricesFilterItems,
  getRecommendedFilterItems,
  getStopsFilterItems,
  getStopoverCitiesFilterItems,
  getStopoverDurationFilterItems,
} from 'lib/flights/flightFilterUtils'
import { getFlightFilterLabelDescriptors } from 'lib/flights/flightFilterLabelUtils'
import defaultIntl from 'i18n/util/defaultIntl'

export type VerticalType = 'car_hire' | 'hotels' | 'tours' | 'cruises' | 'ultralux' | 'experiences' | 'flights' | 'merchandise'

const VerticalTypeMapping: Record<App.SearchVertical, VerticalType> = {
  'car-hire': 'car_hire',
  hotels: 'hotels',
  tours: 'tours',
  cruises: 'cruises',
  experiences: 'experiences',
  flights: 'flights',
  'homes-and-villas': 'hotels', // Temporary until we add tracking for homes-and-villas
  merchandise: 'merchandise',
}

interface DestinationContext {
  destinationType: comluxgroup.SearchContext_1_1_0['destinationType']
  destinationIds: Array<string>
  landmarkIds: Array<string>
  propertyIds: Array<string>
}

interface SearchItemContext {
  destinationType: comluxgroup.SearchContext_1_1_0['destinationType']
  locations: Array<string>
  destinationIds: Array<string>
  landmarkIds: Array<string>
  propertyIds: Array<string>
}

function getDestinationType(item: App.SearchItem | undefined): DestinationContext {
  const empty = { destinationIds: [], landmarkIds: [], propertyIds: [] }

  if (!item) return { ...empty, destinationType: 'location' }

  const effectiveSearchType: string | undefined = item.searchType === 'searchItemV3'
    ? item.searchItem.extractedData.searchType
    : item.searchType

  if (effectiveSearchType === 'property') {
    const propertyId = item.searchType === 'searchItemV3'
      ? ('property' in item.searchItem.extractedData ? item.searchItem.extractedData.property?.propertyId : undefined)
      : item.value
    return { ...empty, destinationType: 'property', propertyIds: propertyId ? [propertyId] : [] }
  }

  if (effectiveSearchType === 'landmark' || effectiveSearchType === 'airport' || effectiveSearchType === 'train_station' || effectiveSearchType === 'metro_station') {
    const landmarkIds = item.searchType === 'searchItemV3'
      ? ('placeIds' in item.searchItem.extractedData ? item.searchItem.extractedData.placeIds ?? [] : [])
      : item.value ? [item.value] : []
    return { ...empty, destinationType: 'landmark', landmarkIds }
  }

  const destinationIds = item.searchType === 'searchItemV3'
    ? ('placeIds' in item.searchItem.extractedData ? item.searchItem.extractedData.placeIds ?? [] : [])
    : item.value ? [item.value] : []
  return { ...empty, destinationType: 'location', destinationIds }
}

function getSearchItemContext(items: Array<App.SearchItem | undefined>): SearchItemContext {
  const contexts = items.map(item => getDestinationType(item))

  return {
    destinationType: contexts[0]?.destinationType ?? 'location',
    locations: items.map(item => item?.format.mainText).filter(text => text !== undefined),
    destinationIds: contexts.flatMap(context => context.destinationIds),
    landmarkIds: contexts.flatMap(context => context.landmarkIds),
    propertyIds: contexts.flatMap(context => context.propertyIds),
  }
}

type AccommodationSearchContextOverrides = {
  overrideDestinationIds?: Array<string>
  overrideDestinationType?: 'location' | 'property' | 'landmark'
  overridePropertyIds?: Array<string>
  overrideLandmarkIds?: Array<string>
}

export function getSearchContextOverrides(
  filters: Pick<App.OfferListFilters, 'destinationIds' | 'destinationId' | 'propertyId' | 'landmarkId' | 'landmarkIds'>,
): AccommodationSearchContextOverrides {
  const destinationIds = filters.destinationIds ?? (filters.destinationId ? [filters.destinationId] : undefined)
  const landmarkIds = filters.landmarkIds ?? (filters.landmarkId ? [filters.landmarkId] : undefined)

  if (filters.propertyId) {
    return {
      overrideDestinationType: 'property',
      overridePropertyIds: [filters.propertyId],
    }
  }

  if (landmarkIds?.length) {
    return {
      overrideDestinationType: 'landmark',
      overrideLandmarkIds: landmarkIds,
    }
  }

  if (destinationIds?.length) {
    return {
      overrideDestinationType: 'location',
      overrideDestinationIds: destinationIds,
    }
  }

  return {}
}

function getDateType(
  checkinDate?: moment.Moment | string,
  checkoutDate?: moment.Moment | string,
  flexibleNights?: boolean | FLEXIBLE_DURATION_RANGE,
  flexibleMonths?: string | null,
  durationRange?: string | Array<any> | null,
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS,
): comluxgroup.SearchContext_1_1_0['dateType'] {
  if (checkinDate && checkoutDate) {
    return 'specific'
  } else if (flexibleNights || (flexibleMonths || durationRange?.length) || dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
    return 'flexible'
  } else {
    return 'anytime'
  }
}

function getDriverAge(driversAgeCategory?: App.CarHireDriverAgeCategory): comluxgroup.SearchContext_1_1_0['driverAge'] {
  if (driversAgeCategory === 1) {
    return '18-29'
  } else if (driversAgeCategory === 2) {
    return '30-69'
  } else if (driversAgeCategory === 3) {
    return '70+'
  } else {
    return undefined
  }
}

function isValidFlightDate(dateStr?: string): boolean {
  if (!dateStr) {
    return false
  }

  const date = Date.parse(dateStr)
  return !isNaN(date)
}

export const mapGlobalSearchContextToSnowplowSearchEvent = (
  context: Partial<GlobalSearchState> & {
    overrideDestinationIds?: Array<string>,
    overrideDestinationType?: 'location' | 'property' | 'landmark'
    overridePropertyIds?: Array<string>
    overrideLandmarkIds?: Array<string>
  },
): comluxgroup.SearchContext_1_1_1 => {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    secondarySearchItems,
    occupancies,
    searchVerticals,
    flexibleNights,
    flexibleMonths,
    durationRange,
    checkinDate,
    checkoutDate,
    dateSearchOptionId,
    cruiseLines,
    pickUpTime,
    dropOffTime,
    driversAgeCategory,
    eventAnalytics,
    overrideDestinationIds,
    overrideDestinationType,
    overridePropertyIds,
    overrideLandmarkIds,
  } = context

  const searchId = eventAnalytics?.searchId ?? uuidV4()
  const vertical = searchVerticals ? [...searchVerticals][0] : 'hotels'
  const hasOrigin = vertical === 'car-hire' || vertical === 'flights' || vertical === 'cruises'

  const occupants = occupancies ? countOccupants(occupancies) : undefined
  const childrenAges = occupants?.childrenAge?.length ? occupants.childrenAge : undefined
  const driverAge = getDriverAge(driversAgeCategory)
  const checkIn = moment(checkinDate).format(ISO_DATE_FORMAT)
  const checkOut = moment(checkoutDate).format(ISO_DATE_FORMAT)

  const dateType = getDateType(checkinDate, checkoutDate, flexibleNights, flexibleMonths, durationRange, dateSearchOptionId)

  const originInfo = vertical !== 'cruises' ? [searchItem] : (searchItems || [])
  const departureInfo = vertical !== 'cruises' ? [secondarySearchItem] : (secondarySearchItems || [])
  const originContext = getSearchItemContext(originInfo)
  const departureContext = getSearchItemContext(departureInfo)
  const destinationType = overrideDestinationType ?? originContext.destinationType // override > derived > default
  const originLocations = originContext.locations
  const departureLocations = departureContext.locations
  const landmarkIds = overrideLandmarkIds ?? originContext.landmarkIds // URL filters take precedence
  const propertyIds = overridePropertyIds ?? originContext.propertyIds // URL filters take precedence
  const destinationIds = overrideDestinationIds ?? (
    hasOrigin && (secondarySearchItem || secondarySearchItems)
      ? departureContext.destinationIds
      : originContext.destinationIds
  ) // round-trip/cruise uses departure leg
  const naturalLanguageQuery = vertical === 'hotels'
    ? ((searchItem?.searchType === 'searchItemV3' && searchItem?.display?.searchType === 'naturalLanguage'
        ? searchItem?.value
        : undefined) ?? searchItem?.format.mainText)
    : undefined

  return {
    adults: occupants?.adults,
    children: (occupants?.children ?? 0) + (occupants?.infants ?? 0) || undefined,
    childrenAges,
    rooms: occupancies?.length || undefined,
    occupancy: occupancies?.length ? occupancies.map(room => ({
      adults: room.adults ?? null,
      ...(room.childrenAge?.length ? { childrenAges: room.childrenAge } : {}),
    })) : null,
    dateType,
    startDate: checkinDate ? checkIn : undefined,
    endDate: checkoutDate ? checkOut : undefined,
    flexibleMonths: flexibleMonths ? flexibleMonths.split(',').filter(Boolean) : undefined,
    vertical: VerticalTypeMapping[vertical] as any,
    durationType: (durationRange?.length ? durationRange[0] : (flexibleNights === '1-30' ? 'any' : flexibleNights)) || undefined,
    destinationType,
    origins: hasOrigin ? originLocations : undefined,
    originIds: hasOrigin ? originContext.destinationIds : undefined,
    destinations: (destinationIds.length || propertyIds.length || landmarkIds.length) ? undefined : (hasOrigin && departureLocations.length ? departureLocations : originLocations),
    destinationIds: destinationIds.length ? destinationIds : undefined,
    cruiseLines: cruiseLines?.length ? cruiseLines : undefined,
    pickUpTime,
    dropOffTime,
    driverAge,
    searchId,
    naturalLanguageQuery,
    landmarkIds: landmarkIds.length ? landmarkIds : undefined,
    propertyIds: propertyIds.length ? propertyIds : undefined,
  }
}

export function mapRecentSearchDataToSnowplowSearchEvent(
  recentSearch: App.FullRecentSearch,
  searchVerticals: Set<App.SearchVertical>,
): comluxgroup.SearchContext_1_1_0 {
  const {
    searchItem,
    rooms,
    dates,
  } = recentSearch
  const vertical = searchVerticals ? [...searchVerticals][0] : 'hotels'
  const occupants = countOccupants(recentSearch.rooms)
  const isFlexible = !!dates.flexibleMonths || !!dates.flexibleDuration
  const dateType = getDateType(dates?.checkIn, dates?.checkOut, isFlexible)
  const { destinationType, destinationIds, landmarkIds, propertyIds } = getDestinationType(searchItem)

  return {
    adults: occupants.adults,
    children: occupants.children,
    rooms: rooms?.length || 0,
    dateType,
    startDate: dates?.checkIn || undefined,
    endDate: dates?.checkOut || undefined,
    vertical: VerticalTypeMapping[vertical] as any,
    durationType: (dates?.flexibleDuration === '1-30' ? 'any' : dates?.flexibleDuration) || undefined,
    destinationType,
    destinations: (destinationIds.length || propertyIds.length || landmarkIds.length) ? undefined : [searchItem?.format.mainText],
    destinationIds: destinationIds.length ? destinationIds : undefined,
    landmarkIds: landmarkIds.length ? landmarkIds : undefined,
    propertyIds: propertyIds.length ? propertyIds : undefined,
    searchId: uuidV4(),
  }
}

type flightType = 'return' | 'one_way' | 'multi_city'

function mapFlightFareType(fareType?: FlightsFareTypes): flightType {
  switch (fareType) {
    case FlightsFareTypes.ONE_WAY:
      return 'one_way'

    case FlightsFareTypes.RETURN:
      return 'return'

    case FlightsFareTypes.MULTI_CITY:
      return 'multi_city'

    default:
      // For backward compatibility
      return 'return'
  }
}

interface FlightSearchSearchData {
  searchVerticals: Set<App.SearchVertical>
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departDate: string
  returnDate?: string
  occupancies: App.Occupants
  fareClass?: FlightsClassTypes
  fareType?: FlightsFareTypes
}

export interface FlightSearchItineraryLeg {
  departureAirport: string
  arrivalAirport: string
  departureDate: string
}

export interface FlightSearchParamsSearchContextData {
  itinerary: Array<FlightSearchItineraryLeg>
  occupancies: Array<App.Occupants>
  fareClass?: string
  fareType?: string
  searchId?: string
}

function mapFlightSearchFareClass(fareClass?: string): comluxgroup.SearchContext_1_1_0['flightClass'] {
  if (fareClass === FlightsClassTypes.LUXROW || fareClass === FlightsClassTypes.LUXROW_PAX_ONE) {
    return 'economy'
  }

  return fareClass as comluxgroup.SearchContext_1_1_0['flightClass']
}

export function mapFlightSearchParamsToSnowplowSearchContext(
  data: FlightSearchParamsSearchContextData,
): comluxgroup.SearchContext_1_1_0 {
  const {
    itinerary,
    occupancies,
    fareClass,
    fareType,
    searchId,
  } = data
  const firstLeg = itinerary[0]
  const lastLeg = itinerary[itinerary.length - 1]
  const occupants = countOccupantsForFlights(occupancies)

  // SearchContext only supports a single origin/destination pair (first leg). For return
  // trips that is the outbound route; for multi-city it is incomplete — use flight_itinerary
  // context on flight search completion for the journey summary.
  return {
    adults: occupants.adults,
    children: occupants.children,
    startDate: isValidFlightDate(firstLeg?.departureDate) ? firstLeg.departureDate : undefined,
    endDate: itinerary.length > 1 && isValidFlightDate(lastLeg?.departureDate) ? lastLeg.departureDate : undefined,
    vertical: 'flights',
    destinationType: 'location',
    origins: firstLeg?.departureAirport ? [firstLeg.departureAirport] : [],
    originIds: firstLeg?.departureAirport ? [firstLeg.departureAirport] : [],
    destinations: firstLeg?.arrivalAirport ? [firstLeg.arrivalAirport] : [],
    destinationIds: firstLeg?.arrivalAirport ? [firstLeg.arrivalAirport] : [],
    flightClass: mapFlightSearchFareClass(fareClass),
    flightType: mapFlightFareType(fareType as FlightsFareTypes),
    searchId: searchId ?? uuidV4(),
  }
}

export function mapFlightSearchParamsToFlightItineraryContext(
  data: Pick<FlightSearchParamsSearchContextData, 'itinerary' | 'fareType'>,
): comluxgroup.FlightItinerary_1_0_0 {
  const { itinerary, fareType } = data
  const firstLeg = itinerary[0]
  const lastLeg = itinerary[itinerary.length - 1]
  const isReturnFlight = fareType === FlightsFareTypes.RETURN
  const arrivalAirportCode = isReturnFlight ? firstLeg?.arrivalAirport : lastLeg?.arrivalAirport

  return {
    itineraryType: mapFlightFareType(fareType as FlightsFareTypes),
    itineraryId: uuidV4(),
    departureAirportCode: firstLeg?.departureAirport,
    arrivalAirportCode,
  }
}

export function mapFlightSearchDataToSnowplowSearchEvent(data: FlightSearchSearchData): comluxgroup.SearchContext_1_1_0 {
  const {
    searchVerticals,
    originAirportCode,
    originAirportName,
    destinationAirportCode,
    destinationAirportName,
    departDate,
    returnDate,
    occupancies,
    fareClass,
    fareType,
  } = data
  return {
    adults: occupancies?.adults,
    children: occupancies?.children,
    startDate: isValidFlightDate(departDate) ? departDate : undefined,
    endDate: isValidFlightDate(returnDate) ? returnDate : undefined,
    vertical: VerticalTypeMapping[(searchVerticals ? [...searchVerticals][0] : 'flights')] as any,
    destinationType: 'location',
    origins: originAirportName ? [originAirportName] : [],
    originIds: originAirportCode ? [originAirportCode] : [],
    destinations: (destinationAirportName ? [destinationAirportName] : []),
    destinationIds: (destinationAirportCode ? [destinationAirportCode] : []),
    flightClass: mapFlightSearchFareClass(fareClass),
    flightType: mapFlightFareType(fareType),
    searchId: uuidV4(),
  }
}

export function mapCruiseLineFilterToSnowplowSearchEvent(search: GlobalSearchState): comluxgroup.SearchContext_1_1_0 {
  return mapGlobalSearchContextToSnowplowSearchEvent({
    searchItems: search.searchItems,
    secondarySearchItems: search.secondarySearchItems,
    flexibleMonths: search.flexibleMonths,
    durationMin: search.durationMin,
    durationMax: search.durationMax,
    durationRange: search.durationRange,
    checkinDate: search.checkinDate,
    checkoutDate: search.checkoutDate,
    cruiseLines: search.cruiseLines,
    searchVerticals: new Set(['cruises']),
  })
}

export function mapOfferListFiltersToFilterContexts({
  offerListFilters,
  CUSTOMER_RATING_GTE,
}: {
  offerListFilters: App.OfferListFilters
  CUSTOMER_RATING_GTE: Record<string, { value: number, label: string }>
}): Array<Filter_1_0_0> {
  const filterContexts: Array<Filter_1_0_0> = []
  const {
    holidayTypes,
    propertyTypes,
    amenities,
    bedroomsGte,
    bedsGte,
    bathroomsGte,
    customerRatingGte,
    priceGte,
    priceLte,
    luxPlusFeatures,
    inclusions,
    tags,
    chains,
    subChains,
    inclusionGap,
  } = offerListFilters

  const addFilters = ({ items, type }: { items: Array<string> | undefined, type: string }) => {
    if (!items?.length) return
    items.forEach(item => {
      filterContexts.push({
        label: item,
        type,
        value: item,
      })
    })
  }

  const addNumericFilter = ({
    value,
    label,
    type = 'Rooms',
  }: {
    value: number | undefined
    label: string
    type?: string
  }) => {
    if (value === undefined) return
    filterContexts.push({
      label,
      type,
      value,
    })
  }

  addFilters({ items: holidayTypes, type: 'Type of escape' })
  addFilters({ items: propertyTypes, type: 'Accommodation type' })
  addFilters({ items: amenities, type: 'Amenities' })
  addFilters({ items: inclusions, type: 'Inclusions' })
  addFilters({ items: luxPlusFeatures, type: 'LuxPlus+' })
  addFilters({ items: tags, type: 'Tags' })
  addFilters({ items: chains, type: 'Property brands' })
  addFilters({ items: subChains, type: 'Sub brands' })
  addFilters({ items: inclusionGap ? [inclusionGap] : undefined, type: 'Inclusion gap' })
  addNumericFilter({ value: bedroomsGte, label: 'Bedrooms' })
  addNumericFilter({ value: bedsGte, label: 'Beds' })
  addNumericFilter({ value: bathroomsGte, label: 'Bathrooms' })

  // Add customer rating
  if (customerRatingGte !== undefined) {
    const ratingEntry =
      Object.values(CUSTOMER_RATING_GTE).find(entry => entry.value === customerRatingGte)

    filterContexts.push({
      label: ratingEntry?.label ?? `Rating: ${customerRatingGte}+`,
      type: 'Customer rating',
      value: customerRatingGte,
    })
  }

  // Add price filters
  if (priceGte !== undefined) {
    filterContexts.push({
      label: 'priceGte',
      type: 'Price',
      value: priceGte,
    })
  }

  if (priceLte !== undefined) {
    filterContexts.push({
      label: 'priceLte',
      type: 'Price',
      value: priceLte,
    })
  }

  return filterContexts
}

export function mapStandaloneFlightFiltersToFilterContexts({
  journeys,
  filtersState,
  airports,
  currency,
  region,
}: {
  journeys: Array<App.JourneyV2>
  filtersState: StandaloneFlightFiltersState
  airports?: { origin?: App.Airport, destination?: App.Airport }
  currency: string
  region: string
}): Array<Filter_1_0_0> {
  if (!filtersState) return []

  const filterItems = buildStandaloneFlightFilterItems(journeys, filtersState.priceFilterType)
  const activeFilters = buildStandaloneFlightActiveFilters(filtersState)

  const descriptors = getFlightFilterLabelDescriptors({
    filters: filterItems,
    activeFilters,
    airports: {
      origin: airports?.origin?.code ?? '',
      arrival: airports?.destination?.code ?? '',
    },
    currency: { code: currency, region },
    priceFilterType: filtersState.priceFilterType,
    intl: defaultIntl,
  })

  return descriptors
    .filter(descriptor => descriptor.type !== FlightFilterType.RECOMMENDED)
    .map(descriptor => ({
      label: descriptor.label,
      type: descriptor.type,
      value: formatFilterValue(descriptor.values),
    }))
}

function buildStandaloneFlightFilterItems(
  journeys: Array<App.JourneyV2>,
  priceFilterType: StandaloneFlightFiltersState['priceFilterType'],
) {
  return {
    [FlightFilterType.STOPS]: getStopsFilterItems(journeys),
    [FlightFilterType.CABIN_TYPES]: getCabinTypeFilterItems(journeys),
    [FlightFilterType.STOP_DURATIONS]: getStopoverDurationFilterItems(journeys),
    [FlightFilterType.AIRLINES]: getAirlineFilterItems(journeys),
    [FlightFilterType.DURATIONS]: getDurationFilterItems(journeys),
    [FlightFilterType.PRICES]: getPricesFilterItems(priceFilterType, journeys),
    [FlightFilterType.DEPARTURE_TIMES]: getDepartureTimesFilterItems(journeys),
    [FlightFilterType.ARRIVAL_TIMES]: getArrivalTimesFilterItems(journeys),
    [FlightFilterType.RECOMMENDED]: getRecommendedFilterItems(journeys),
    [FlightFilterType.BAGS]: getBagsFilterItems(journeys),
    [FlightFilterType.STOP_OVER_CITIES]: getStopoverCitiesFilterItems(journeys),
    [FlightFilterType.ALLIANCES]: getAllianceFilterItems(journeys),
  }
}

function buildStandaloneFlightActiveFilters(filtersState: StandaloneFlightFiltersState) {
  const withAll = (values: Array<string>) => (values.length ? values : ['all'])
  const singleValueSet = (value?: string) => (value ? new Set([value]) : new Set<string>())

  return {
    [FlightFilterType.STOPS]: new Set(withAll(filtersState.stops)),
    [FlightFilterType.CABIN_TYPES]: new Set(withAll(filtersState.cabinTypes)),
    [FlightFilterType.STOP_DURATIONS]: singleValueSet(filtersState.stopDurations),
    [FlightFilterType.AIRLINES]: new Set(withAll(filtersState.airlines)),
    [FlightFilterType.DURATIONS]: singleValueSet(filtersState.durations),
    [FlightFilterType.MIN_PRICES]: singleValueSet(filtersState.minPrices),
    [FlightFilterType.MAX_PRICES]: singleValueSet(filtersState.maxPrices),
    [FlightFilterType.MIN_DEPARTURE_TIME]: singleValueSet(filtersState.minDepartureTime),
    [FlightFilterType.MAX_DEPARTURE_TIME]: singleValueSet(filtersState.maxDepartureTime),
    [FlightFilterType.MIN_ARRIVAL_TIME]: singleValueSet(filtersState.minArrivalTime),
    [FlightFilterType.MAX_ARRIVAL_TIME]: singleValueSet(filtersState.maxArrivalTime),
    [FlightFilterType.RECOMMENDED]: new Set(filtersState.recommended),
    [FlightFilterType.BAGS]: new Set(filtersState.bags),
    [FlightFilterType.STOP_OVER_CITIES]: new Set(withAll(filtersState.stopOverCities)),
    [FlightFilterType.ALLIANCES]: new Set(withAll(filtersState.alliances)),
  }
}

function formatFilterValue(values: Array<string | number>): string | number | null {
  if (!values.length) return null
  if (values.length === 1) {
    return values[0]
  }

  return values.join(' - ')
}

export function isSortBy(sortBy: string): sortBy is SortContextValue {
  const validSortByValues: Array<SortContextValue> = [
    'value', 'endingsoon', 'recommended', 'price.asc', 'price.desc',
    'departure_date.asc', 'updated_at.desc', 'customerRating',
    'numberOfPlacesVisited.asc', 'numberOfPlacesVisited.desc',
    'tourLength.asc', 'tourLength.desc', 'updated_at.asc', 'departure_date.desc',
  ]
  return validSortByValues.includes(sortBy as SortContextValue)
}

type SortContextValue =
  | 'value'
  | 'endingsoon'
  | 'recommended'
  | 'price.asc'
  | 'price.desc'
  | 'departure_date.asc'
  | 'updated_at.desc'
  | 'customerRating'
  | 'numberOfPlacesVisited.asc'
  | 'numberOfPlacesVisited.desc'
  | 'tourLength.asc'
  | 'tourLength.desc'
  | 'updated_at.asc'
  | 'departure_date.desc'

export function mapOfferListFiltersToSortContext({
  offerListFilters,
}: {
  offerListFilters: App.OfferListFilters
}): Sort_1_0_0 | undefined {
  const { sortBy = 'recommended' } = offerListFilters

  if (!isSortBy(sortBy)) return undefined

  return { value: sortBy }
}

export function mapSearchSessionSearchEventToSearchEvent({
  searchEvent,
}: {
  searchEvent?: App.SearchEvent
}): Search_3_0_0 {
  if (!searchEvent) return { type: 'page_landing' }

  return { type: searchEvent.type }
}
